import SubHeaders from '@/components/Headers/SubHeaders'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import {
    getSupplier,
    deleteSupplier,
    toggleSupplierActive,
} from '../../services'
import { paths } from '@/utils/path'
import { useParams, useNavigate } from 'react-router-dom'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { GridInformation } from 'vnetwork-ui'
import { Badge } from '@/components/ui/badge'
import { formatDateTime } from '../../utils'
import { useConfirmation } from '@/contexts/confirmation'
import { Edit, Trash2 } from 'lucide-react'

const SupplierDetail = () => {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const { setConfirmation } = useConfirmation()

    const { data: supplier, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.WAREHOUSE_SUPPLIERS, id],
        queryFn: () => getSupplier(id!),
    })

    const { mutate: deleteSupplierMutation, isPending: isDeleting } =
        useMutationWithHandler(
            {
                mutationFn: () => deleteSupplier(id!),
            },
            {
                successMessage: 'Supplier deleted successfully',
                reactQueryKeys: QUERY_KEYS.WAREHOUSE_SUPPLIERS,
                onSuccess: () => {
                    navigate(paths.warehouseSuppliers)
                },
            }
        )

    const { mutate: toggleActive, isPending: isToggling } =
        useMutationWithHandler(
            {
                mutationFn: () => toggleSupplierActive(id!),
            },
            {
                successMessage: 'Supplier status updated',
                reactQueryKeys: [QUERY_KEYS.WAREHOUSE_SUPPLIERS, id],
            }
        )

    const handleDelete = () => {
        setConfirmation({
            open: true,
            title: 'Delete Supplier',
            description: `Are you sure you want to delete "${supplier?.name}"? This action cannot be undone.`,
            onConfirm: async () => {
                deleteSupplierMutation()
            },
        })
    }

    // Render loading/error state at the end (after all hooks)
    if (renderState()) return renderState()

    return (
        <div>
            <SubHeaders
                actions={[
                    {
                        label: supplier?.is_active ? 'Deactivate' : 'Activate',
                        onClick: () => toggleActive(),
                        disabled: isToggling,
                    },
                    {
                        icon: <Edit className="w-4 h-4" />,
                        label: 'Edit',
                        onClick: () =>
                            navigate(`${paths.warehouseSuppliers}/${id}/edit`),
                    },
                    {
                        icon: <Trash2 className="w-4 h-4" />,
                        label: 'Delete',
                        onClick: handleDelete,
                        disabled: isDeleting,
                        variant: 'destructive',
                    },
                ]}
                breadcrumbs={[
                    {
                        path: paths.warehouse,
                        title: 'Capacities & Resources',
                    },
                    {
                        path: paths.warehouseSuppliers,
                        title: 'Suppliers',
                    },
                    {
                        path: '',
                        title: supplier?.name || '',
                    },
                ]}
            />
            <GridInformation
                data={[
                    {
                        label: 'Supplier Code',
                        value: supplier?.code || '-',
                    },
                    {
                        label: 'Supplier Name',
                        value: supplier?.name || '-',
                    },
                    {
                        label: 'Contact Person',
                        value: supplier?.contact_person || '-',
                    },
                    {
                        label: 'Email',
                        value: supplier?.email || '-',
                    },
                    {
                        label: 'Phone',
                        value: supplier?.phone || '-',
                    },
                    {
                        label: 'Tax Code',
                        value: supplier?.tax_code || '-',
                        hidden: !supplier?.tax_code,
                    },
                    {
                        label: 'Website',
                        value: supplier?.website ? (
                            <a
                                href={supplier.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline"
                            >
                                {supplier.website}
                            </a>
                        ) : (
                            '-'
                        ),
                        hidden: !supplier?.website,
                    },
                    {
                        label: 'Address',
                        value: supplier?.address || '-',
                        className: 'lg:col-span-2',
                        hidden: !supplier?.address,
                    },
                    {
                        label: 'Notes',
                        value: supplier?.notes || '-',
                        className: 'lg:col-span-2',
                        hidden: !supplier?.notes,
                    },
                    {
                        label: 'Status',
                        value: (
                            <Badge
                                variant={
                                    supplier?.is_active
                                        ? 'default'
                                        : 'destructive'
                                }
                            >
                                {supplier?.is_active ? 'ACTIVE' : 'INACTIVE'}
                            </Badge>
                        ),
                    },
                    {
                        label: 'Created At',
                        value: formatDateTime(supplier?.created_at),
                    },
                    {
                        label: 'Updated At',
                        value: formatDateTime(supplier?.updated_at),
                    },
                ]}
                head={{
                    title: 'Supplier Information',
                }}
            />
        </div>
    )
}

export default SupplierDetail
